/**
 * The portal home page's welcome text: a small Markdown subset parsed into a token tree the shells render as their own
 * elements (docs/features/portal-theme/03_security_review.md M3). Nothing here or downstream turns the text into HTML.
 *
 * Supported:
 * - paragraphs (separated by a blank line; a single line break stays a line break);
 * - bulleted lists (`- item` or `* item`) and numbered lists (`1. item` or `1) item`);
 * - `**bold**`, `*italic*` and `_italic_`;
 * - links `[text](url)` to `https:` and `mailto:` only. A link to any other scheme (`javascript:`, `data:`, `http:`)
 *   keeps its text and loses its address.
 *
 * Anything else, raw HTML included, is literal text: the shell renders it as text, so `<script>` shows as the
 * characters `<script>`. The server already cleans the text on save; this is the second line, not the first.
 */

export type WelcomeInline =
  | { kind: "text"; text: string }
  | { kind: "line_break" }
  | { kind: "bold"; children: WelcomeInline[] }
  | { kind: "italic"; children: WelcomeInline[] }
  | { kind: "link"; href: string; children: WelcomeInline[] };

export type WelcomeBlock =
  | { kind: "paragraph"; children: WelcomeInline[] }
  | { kind: "bulleted_list"; items: WelcomeInline[][] }
  | { kind: "numbered_list"; items: WelcomeInline[][] };

const ALLOWED_LINK = /^(https:\/\/|mailto:)\S+$/i;

/** Whether a link may keep its address: `https:` and `mailto:` only. */
export function isAllowedWelcomeLink(href: string): boolean {
  return ALLOWED_LINK.test(href.trim());
}

const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** Parse welcome text into blocks. Pure; never throws. */
export function parseWelcomeText(source: string): WelcomeBlock[] {
  const blocks: WelcomeBlock[] = [];
  let paragraph: string[] = [];
  let list: {
    kind: "bulleted_list" | "numbered_list";
    items: string[];
  } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const children: WelcomeInline[] = [];
    paragraph.forEach((line, index) => {
      if (index > 0) children.push({ kind: "line_break" });
      children.push(...parseInline(line, true));
    });
    blocks.push({ kind: "paragraph", children });
    paragraph = [];
  };
  const flushList = () => {
    if (list === null) return;
    blocks.push({
      kind: list.kind,
      items: list.items.map((item) => parseInline(item, true)),
    });
    list = null;
  };

  for (const rawLine of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trimEnd();
    if (line.trim() === "") {
      flushParagraph();
      flushList();
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const listKind = bullet
      ? "bulleted_list"
      : numbered
        ? "numbered_list"
        : null;
    if (listKind) {
      flushParagraph();
      if (list === null || list.kind !== listKind) {
        flushList();
        list = { kind: listKind, items: [] };
      }
      list.items.push((bullet ?? numbered)![1]);
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}

function pushText(out: WelcomeInline[], text: string): void {
  if (text === "") return;
  const last = out[out.length - 1];
  if (last?.kind === "text") last.text += text;
  else out.push({ kind: "text", text });
}

/** The closing index of an emphasis marker, or -1 when the marker does not open real emphasis. */
function closingMarker(text: string, start: number, marker: string): number {
  const innerStart = start + marker.length;
  if (innerStart >= text.length || /\s/.test(text[innerStart])) return -1;
  let close = text.indexOf(marker, innerStart);
  // `*` inside `**...**` is not italic's close; skip a doubled marker when looking for a single one.
  while (close !== -1 && marker.length === 1 && text[close + 1] === marker) {
    close = text.indexOf(marker, close + 2);
  }
  if (close <= innerStart || /\s/.test(text[close - 1])) return -1;
  return close;
}

function parseInline(text: string, linksAllowed: boolean): WelcomeInline[] {
  const out: WelcomeInline[] = [];
  let i = 0;
  while (i < text.length) {
    if (text.startsWith("**", i)) {
      const close = closingMarker(text, i, "**");
      if (close !== -1) {
        out.push({
          kind: "bold",
          children: parseInline(text.slice(i + 2, close), linksAllowed),
        });
        i = close + 2;
        continue;
      }
    }
    const char = text[i];
    if ((char === "*" || char === "_") && text[i + 1] !== char) {
      const close = closingMarker(text, i, char);
      if (close !== -1) {
        out.push({
          kind: "italic",
          children: parseInline(text.slice(i + 1, close), linksAllowed),
        });
        i = close + 1;
        continue;
      }
    }
    if (char === "[" && linksAllowed) {
      const textEnd = text.indexOf("](", i + 1);
      const urlEnd = textEnd === -1 ? -1 : text.indexOf(")", textEnd + 2);
      if (textEnd !== -1 && urlEnd !== -1) {
        const label = parseInline(text.slice(i + 1, textEnd), false);
        const href = text.slice(textEnd + 2, urlEnd).trim();
        if (isAllowedWelcomeLink(href)) {
          out.push({ kind: "link", href, children: label });
        } else {
          // A link to anything but https: or mailto: keeps its words and loses its address.
          for (const token of label) {
            if (token.kind === "text") pushText(out, token.text);
            else out.push(token);
          }
        }
        i = urlEnd + 1;
        continue;
      }
    }
    pushText(out, char);
    i += 1;
  }
  return out;
}
